import { useMemo } from "react";
import type { FareDetailsV2, TripDetailsV2 } from "@b2bportal/air-shopping-api";
import { useDisruptionStyles, useModuleBEM } from "@b2bportal/core-themes";
import {
  CoreDisruptionComponent,
  type PlatformComponentProps,
} from "@b2bportal/core-types";
import { getFilteredFareDetails } from "@overrides/utilities";
import { FareDetails } from "@tenant/flights";

import clsx from "clsx";
import defaultStyles from "./DisruptionExerciseFlightCardFareList.module.scss";

export interface DisruptionExerciseFlightCardFareListComponentProps
  extends PlatformComponentProps {
  fareIdList?: string[];
  clickedFareId?: string;
  trip: TripDetailsV2;
  fareDetails: Array<FareDetailsV2>;
  sliceIndex: number;
  fareShelfFilter: number[];
  sliceId: string;
  tripId: string;
  fareFormatOptions?: Intl.NumberFormatOptions;
}
export const DisruptionExerciseFlightCardFareList = ({
  clickedFareId,
  fareDetails,
  sliceIndex,
  fareShelfFilter,
  trip,
  className,
}: DisruptionExerciseFlightCardFareListComponentProps) => {
  const styles = useDisruptionStyles(
    CoreDisruptionComponent.DisruptionExerciseFlightCardFareList,
    defaultStyles
  );

  const [block, cn] = useModuleBEM(
    styles,
    CoreDisruptionComponent.DisruptionExerciseFlightCardFareList
  );

  const filteredAndSortedFares = useMemo(() => {
    return getFilteredFareDetails({
      fareDetails,
      selectedFareClassFilters: fareShelfFilter,
      sliceIndex,
      selectedFareId: clickedFareId,
    });
  }, [fareDetails, sliceIndex, clickedFareId, fareShelfFilter]);

  /** Trip leg index (`sliceIndex`) must not be used to index the filtered fare list. */
  const displayFare = useMemo((): FareDetailsV2 | undefined => {
    if (filteredAndSortedFares.length > 0) {
      if (clickedFareId) {
        return (
          filteredAndSortedFares.find((f) => f.id === clickedFareId) ??
          filteredAndSortedFares[0]
        );
      }
      return filteredAndSortedFares[0];
    }
    if (clickedFareId) {
      return fareDetails.find((f) => f.id === clickedFareId) ?? fareDetails[0];
    }
    return fareDetails[0];
  }, [filteredAndSortedFares, clickedFareId, fareDetails]);

  const tripSlice = trip.slices[sliceIndex];
  const fareSliceForLeg = displayFare?.slices?.[sliceIndex];

  if (
    displayFare == null ||
    tripSlice == null ||
    fareSliceForLeg == null ||
    fareDetails.length === 0
  ) {
    return (
      <div className={clsx(block, className)}>
        <ul className={cn("fare-list")} />
      </div>
    );
  }

  return (
    <div className={clsx(block, className)}>
      <ul className={cn("fare-list")}>
        {filteredAndSortedFares.length === 1 && (
          <div className={cn("fare-info")}>
            {displayFare.slices[sliceIndex]?.fareShelf?.brandName ?? ""}
          </div>
        )}
        <div className={cn("fare-details")}>
          <FareDetails
            className={styles["disruption-fare-details"]}
            segments={tripSlice.segmentDetails}
            fareSlice={fareSliceForLeg}
            restrictions={displayFare.restrictions?.[sliceIndex]}
          />
        </div>
      </ul>
    </div>
  );
};
